import { Injectable, inject } from '@angular/core';
import type { PushNotificationsPlugin } from '@capacitor/push-notifications';
import { AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import type { NativeSettingsPlugin } from 'capacitor-native-settings';
import { PUSH_NOTIFICATIONS_INJECTION_TOKEN } from './push-notifications.provider';
import { NATIVE_SETTINGS_TOKEN } from '../../providers/native-settings/native-settings.provider';
import { ProfileService } from '../profile/profile.service';
import { AuthService } from '../auth/auth.service';
import { PlatformService } from '../platform.service';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from '../logger/logger.service';
import { DeeplinkService } from '../deeplink/deeplink.service';
import { UserPreferencesService } from 'src/app/pages/route-alert/services/user-preferences.service';

export type PushAvailability = 'web' | 'granted' | 'blocked';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  private readonly logger = LoggerService.create(PushNotificationsService.name);

  private readonly pushNotifications = inject<PushNotificationsPlugin>(PUSH_NOTIFICATIONS_INJECTION_TOKEN);
  private readonly nativeSettings = inject<NativeSettingsPlugin>(NATIVE_SETTINGS_TOKEN);

  constructor(
    private profileService: ProfileService,
    private authService: AuthService,
    private platformService: PlatformService,
    private deeplinkService: DeeplinkService,
    private userPreferencesService: UserPreferencesService
  ) {}

  addListeners = async () => {
    if (!this.platformService.isNative) {
      return;
    }

    await this.pushNotifications.addListener('registration', async (token) => {
      this.logger.info({
        identifier: token.value,
        message: 'Push notification registration token received',
        method: 'addListeners',
      });

      if (this.authService.authenticated()) {
        this.logger.info({
          identifier: token.value,
          message: 'Updating profile with notification token',
          method: 'addListeners',
        });

        try {
          await firstValueFrom(
            this.profileService.updateProfile({ notification_token: token.value, aggreed_to_notifications: true })
          );
        } catch (error) {
          this.logger.error({
            identifier: token.value,
            message: 'Error updating profile with notification token',
            method: 'addListeners',
            error,
          });
        }
      }
    });

    await this.pushNotifications.addListener('registrationError', async (err) => {
      this.logger.error({
        identifier: '',
        message: 'Push notification registration error',
        method: 'addListeners',
        error: err.error,
      });

      if (this.authService.authenticated()) {
        try {
          await firstValueFrom(this.profileService.updateProfile({ notification_token: err.error }));
        } catch (error) {
          this.logger.error({
            identifier: '',
            message: 'Error updating profile with registration error',
            method: 'addListeners',
            error,
          });
        }
      }
    });

    await this.pushNotifications.addListener('pushNotificationReceived', (notification) => {
      this.logger.info({
        identifier: '',
        message: 'Push notification received',
        method: 'addListeners', notification,
      });
    });

    await this.pushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      this.logger.info({
        identifier: '',
        message: 'Push notification action performed — full payload',
        method: 'addListeners',
        actionId: notification.actionId,
        inputValue: notification.inputValue,
        notificationData: JSON.stringify(notification.notification?.data),
        notificationRaw: JSON.stringify(notification.notification),
      });

      this.deeplinkService.handle(notification.notification?.data, notification.notification);
    });
  };

  registerNotifications = async () => {
    if (!this.platformService.isNative) {
      return;
    }

    let permStatus = await this.pushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await this.pushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      this.logger.warn({
        identifier: '',
        message: 'Push notification registration not possible because permissions are not granted',
        method: 'registerNotifications',
        error: permStatus.receive,
      });

      await this.syncDeniedNotificationState();

      return;
    }

    await this.pushNotifications.register();
  };

  /**
   * Aligns the backend with a not-granted OS push permission: clears the profile
   * token and consent flag, and disables the route alert push preference.
   * No-op when the user is not authenticated.
   */
  private syncDeniedNotificationState = async (): Promise<void> => {
    if (!this.authService.authenticated()) {
      return;
    }

    await this.clearProfileNotificationState();
    await this.updateRouteAlertChannelPreference();
  };

  private clearProfileNotificationState = async (): Promise<void> => {
    try {
      await firstValueFrom(
        this.profileService.updateProfile({ notification_token: '', aggreed_to_notifications: false })
      );

      this.logger.info({
        identifier: '',
        message: 'Cleared notification token and consent because push permission is not granted',
        method: 'clearProfileNotificationState',
      });
    } catch (error) {
      this.logger.error({
        identifier: '',
        message: 'Error clearing profile notification state',
        method: 'clearProfileNotificationState',
        error,
      });
    }
  };

  /**
   * Disables the route alert push preference and keeps email enabled so at least
   * one notification channel stays active when push permission is not granted.
   */
  private updateRouteAlertChannelPreference = async (): Promise<void> => {
    try {
      await this.userPreferencesService.updatePreferences({ routeAlertPush: false, routeAlertEmail: true });

      this.logger.info({
        identifier: '',
        message: 'Disabled route alert push and kept email active because push permission is not granted',
        method: 'updateRouteAlertChannelPreference',
      });
    } catch (error) {
      this.logger.error({
        identifier: '',
        message: 'Error updating route alert channel preference',
        method: 'updateRouteAlertChannelPreference',
        error,
      });
    }
  };

  /**
   * Resolves the current push availability for preference UIs.
   * Returns `web` on non-native platforms, otherwise the OS permission state.
   */
  getPushAvailability = async (): Promise<PushAvailability> => {
    if (!this.platformService.isNative) {
      return 'web';
    }

    const permStatus = await this.pushNotifications.checkPermissions();

    return permStatus.receive === 'granted' ? 'granted' : 'blocked';
  };

  /**
   * Requests OS push permission and registers the device on grant.
   * No-op on non-native platforms.
   *
   * When permission is already denied the OS no longer shows its prompt (iOS only
   * ever shows it once per install), so the user is sent to the native app settings
   * to enable notifications manually.
   *
   * @returns `true` if permission is granted, `false` otherwise.
   */
  requestPushPermission = async (): Promise<boolean> => {
    if (!this.platformService.isNative) {
      return false;
    }

    let permStatus = await this.pushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
      permStatus = await this.pushNotifications.requestPermissions();
    }

    if (permStatus.receive === 'denied') {
      await this.openNotificationSettings();

      return false;
    }

    if (permStatus.receive !== 'granted') {
      return false;
    }

    await this.pushNotifications.register();

    return true;
  };

  /**
   * Opens the native app settings so the user can enable notifications manually.
   * Required on iOS once permission is denied, since the OS consent dialog cannot
   * be re-triggered programmatically.
   */
  private openNotificationSettings = async (): Promise<void> => {
    try {
      await this.nativeSettings.open({
        optionAndroid: AndroidSettings.AppNotification,
        optionIOS: IOSSettings.App,
      });
    } catch (error) {
      this.logger.error({
        identifier: '',
        message: 'Error opening native notification settings',
        method: 'openNotificationSettings',
        error,
      });
    }
  };

  getDeliveredNotifications = async () => {
    const notificationList = await this.pushNotifications.getDeliveredNotifications();

    this.logger.info({
      identifier: '',
      message: 'Delivered notifications retrieved',
      method: 'getDeliveredNotifications',
      notifications: notificationList,
    });
  };
}
